import { GetAllFilters_getFiltersData_primary_filter as PrimaryFilterType } from '@generated/types';
import React, { useState } from 'react';
import { ActiveFilterType, GridFilterModel, OptionType, SavedFilter } from '~/types/AgGridTypes';

export interface FilterState {
  filterValue?: OptionType | null;
  explicitlyClearedFilter?: boolean;
  activeFilters?: ActiveFilterType | null;
  tableState?: string;
  presetFilterChip?: boolean;
  isSavedFilter?: boolean;
  selectedChipValue?: OptionType | null;
  filterColumn?: GridFilterModel;
}

export interface FilterToApply {
  label?: string;
  filter_name?: string;
  value?: string;
  filters?: {
    filterModel?: GridFilterModel | ActiveFilterType;
  };
}

export interface ResolveActiveFilterParams {
  primaryFilter?: PrimaryFilterType | null;
  savedFilters: SavedFilter[];
  presetFilters: OptionType[];
  selectedFilterChip?: OptionType | null;
  url?: string;
}

export interface ResolvedFilterState {
  filterToApply: FilterToApply | undefined;
  filterValue: {
    label: string;
    value: string;
  };
  selectedChipValue?: OptionType | null;
}

export const useFilterState = () => {
  const [filterState, setFilterState] = useState<FilterState>({});
  const updateTableState = (tableState: string) => {
    if (tableState === filterState.tableState) {
      setFilterState((prevState) => ({
        ...prevState,
        isSavedFilter: false,
      }));
    } else {
      setFilterState((prevState) => ({
        ...prevState,
        filterValue: null,
        tableState: tableState,
        isSavedFilter: false,
      }));
    }
  };

  const updateFilterValue = (value: OptionType | null) => {
    setFilterState((prevState) => ({
      ...prevState,
      activeFilters: null,
      filterValue: value,
    }));
  };

  const updateActiveFilters = (activeFilters: ActiveFilterType | null) => {
    setFilterState((prevState) => ({
      ...prevState,
      activeFilters: activeFilters,
    }));
  };

  const updateExplicitlyClearedFilter = (cleared: boolean) => {
    setFilterState((prevState) => ({
      ...prevState,
      explicitlyClearedFilter: cleared,
    }));
  };

  const updatePresetFilter = React.useCallback((isPresetFilterChip: boolean) => {
    setFilterState((prevState) => ({
      ...prevState,
      presetFilterChip: isPresetFilterChip,
    }));
  }, []);

  const updateSearchText = (searchText: string) => {
    setFilterState((prevState) => ({
      ...prevState,
      searchText: searchText,
    }));
  };

  const updateFilterColumn = (filterColumn: GridFilterModel) => {
    setFilterState((prevState) => ({
      ...prevState,
      filterColumn: filterColumn,
    }));
  };

  const getFilterState = () => {
    return filterState;
  };

  const updateSavedFilter = (saved: boolean) => {
    setFilterState((prevState) => ({
      ...prevState,
      activeFilters: prevState.activeFilters,
      isSavedFilter: saved,
    }));
  };

  const resolveActiveFilter = (params: ResolveActiveFilterParams): ResolvedFilterState => {
    const { primaryFilter, savedFilters, presetFilters, selectedFilterChip, url } = params;
    const filterParams = new URLSearchParams(url || '');
    const filterQueryValue = filterParams.get('query');

    let filterToApply: FilterToApply | undefined;

    if ((filterState.filterValue?.value && !filterQueryValue) || (filterState.filterValue?.value && filterQueryValue)) {
      filterToApply = savedFilters?.find(
        (filter: { filter_name: string }) => filter.filter_name === filterState.filterValue?.value,
      );
      filterToApply ??= presetFilters?.find(
        (filter: { value: string }) => filter.value === filterState.filterValue?.value,
      );
    }

    if (!filterToApply && primaryFilter?.custom_filter_id && !filterQueryValue) {
      filterToApply = savedFilters.find((filter: { id: string }) => filter.id === primaryFilter.custom_filter_id);
    } else if (!filterToApply) {
      const key = filterQueryValue ?? primaryFilter?.filter_query_name ?? selectedFilterChip?.value;
      if (key) {
        filterToApply = presetFilters?.find((filter: { value: string }) => filter.value === key);
      } else {
        filterToApply = presetFilters?.[0];
      }
    }

    const selectedChipValue = filterState.filterValue?.value === selectedFilterChip?.value ? selectedFilterChip : null;
    const filterValue = {
      label: filterToApply?.label ?? filterToApply?.filter_name ?? filterState.filterValue?.label ?? '',
      value: filterToApply?.value ?? filterToApply?.filter_name ?? filterState.filterValue?.value ?? '',
    };
    const selectedFilterModel =
      typeof filterToApply?.filters === 'string' ? JSON.parse(filterToApply.filters) : filterToApply?.filters;
    const appliedFilterModel = selectedFilterModel?.filterModel;
    if (!filterState.activeFilters || appliedFilterModel) {
      updateActiveFilters(appliedFilterModel as ActiveFilterType);
    }

    if (filterState.filterValue?.value !== filterValue.value) {
      setFilterState((prevState) => ({
        ...prevState,
        filterValue: filterValue,
        selectedChipValue,
      }));
    }

    return {
      filterToApply,
      filterValue,
      selectedChipValue,
    };
  };

  return {
    filterState,
    updatePresetFilter,
    updateFilterValue,
    updateExplicitlyClearedFilter,
    resolveActiveFilter,
    updateTableState,
    updateActiveFilters,
    getFilterState,
    updateSavedFilter,
    updateSearchText,
    updateFilterColumn,
  };
};
